<template>
  <nav class="premium-navbar">
    <div class="premium-navbar__inner">
      <div class="premium-navbar__top">
        <div class="brand-group">
          <b-navbar-brand to="/" class="brand-mark">
            <img
              src="@/assets/ministryOfHealthLogo.png"
              alt="Ministry of Health"
              class="brand-mark__image"
            />
          </b-navbar-brand>

          <div class="brand-divider"></div>

          <router-link to="/" class="brand-copy">
            <span class="brand-copy__eyebrow">Federal Ministry of Health</span>
            <span class="brand-copy__title">Health Partners' Projects & Data Mapping Platform</span>
            <span class="brand-copy__subtitle">
              Mapping health partners' projects and data-related activities in Nigeria
            </span>
          </router-link>
        </div>

        <div class="topbar-controls">
          <div v-if="isLoggedIn" class="position-relative action-shell mobile-notification-shell">
            <button type="button" class="icon-button px-3" @click="toggleNotification">
              <i class="far fa-bell"></i>
              <span class="notification-count">{{ notifications.length }}</span>
            </button>

            <div v-if="showNotifications" class="overlay-panel notifications-wrapper">
              <NotificationPopup :notifications="notifications" @close="closeNotification" />
            </div>
          </div>

          <button
            type="button"
            class="mobile-nav-toggle"
            :aria-label="mobileNavOpen ? 'Close navigation menu' : 'Open navigation menu'"
            :aria-expanded="mobileNavOpen ? 'true' : 'false'"
            @click="toggleMobileNav"
          >
            <i :class="mobileNavOpen ? 'fas fa-times' : 'fas fa-bars'" aria-hidden="true"></i>
            <span>{{ mobileNavOpen ? 'Close' : 'Menu' }}</span>
          </button>

          <button
            v-if="showMobileAdminMenuButton"
            type="button"
            class="mobile-admin-menu"
            :aria-label="adminMenuOpen ? 'Close admin menu' : 'Open admin menu'"
            @click="$emit('toggle-admin-menu')"
          >
            <i :class="adminMenuOpen ? 'fas fa-times' : 'fas fa-bars'" aria-hidden="true"></i>
            <span>{{ adminMenuOpen ? 'Close menu' : 'Admin menu' }}</span>
          </button>
        </div>
      </div>

      <div class="primary-nav" aria-label="Primary">
        <router-link
          v-for="link in navLinks"
          :key="link.link"
          :to="link.link"
          class="primary-nav__link"
        >
          {{ link.title }}
        </router-link>
      </div>

      <div class="nav-actions">
        <div v-if="isLoggedIn" class="position-relative action-shell">
          <button type="button" class="icon-button px-3" @click="toggleNotification">
            <i class="far fa-bell"></i>
            <span class="notification-count">{{ notifications.length }}</span>
          </button>

          <div v-if="showNotifications" class="overlay-panel notifications-wrapper">
            <NotificationPopup :notifications="notifications" @close="closeNotification" />
          </div>
        </div>

        <div v-if="isLoggedIn" class="position-relative action-shell action-shell--user">
          <button type="button" class="user-wrapper" @click="toggleUserDetails">
            <span class="user-avatar">{{ userInitials }}</span>
            <span class="user-meta">
              <span class="user-meta__label">Signed in as</span>
              <span class="user-meta__name">{{ getName }}</span>
            </span>
            <i class="fas fa-chevron-down user-meta__chevron"></i>
          </button>

          <div
            v-if="showUserDropdown"
            class="overlay-panel user-detail-wrapper"
            @mouseleave="closeUserDetails"
          >
            <div class="user-item-details user-item-details--profile" @click="navigateToHome()">
              <p>{{ getName }}</p>
              <span>{{ getEmail }}</span>
            </div>
            <div v-if="canManageProfile" class="user-item-details" @click="navigateToProfile()">
              <p>Manage Profile</p>
              <span>Update your username, email, and password</span>
            </div>
            <div class="user-item-details" @click="$router.push('/')">
              <p>Go To Homepage</p>
              <span>Return to the public landing page</span>
            </div>
            <div class="user-logout" @click="logoutButton">
              <span><i class="fas fa-sign-out-alt"></i> Logout</span>
            </div>
          </div>
        </div>

        <div v-if="!isLoggedIn && !isAdmin" class="guest-login">
          <router-link to="/auth/login" class="guest-login__link">
            <span>Login</span>
            <i class="fas fa-arrow-right"></i>
          </router-link>
        </div>
      </div>
    </div>

    <transition name="mobile-nav-fade">
      <div v-if="mobileNavOpen" class="mobile-nav-panel">
        <div class="mobile-nav-links" aria-label="Mobile primary navigation">
          <router-link
            v-for="link in navLinks"
            :key="`mobile-${link.link}`"
            :to="link.link"
            class="mobile-nav-link"
            @click.native="closeMobileNav"
          >
            <span>{{ link.title }}</span>
            <i class="fas fa-arrow-right" aria-hidden="true"></i>
          </router-link>
        </div>

        <div class="mobile-nav-meta">
          <router-link
            v-if="!isLoggedIn && !isAdmin"
            to="/auth/login"
            class="mobile-nav-cta"
            @click.native="closeMobileNav"
          >
            Login to your account
          </router-link>
          <button
            v-else-if="isLoggedIn && !isAdmin"
            type="button"
            class="mobile-nav-cta mobile-nav-cta--button"
            @click="handleMobileDashboard"
          >
            Go to dashboard
          </button>

          <div v-if="isLoggedIn" class="mobile-user-panel">
            <div class="mobile-user-panel__summary">
              <span class="user-avatar">{{ userInitials }}</span>
              <div class="mobile-user-panel__meta">
                <span class="mobile-user-panel__label">Signed in as</span>
                <strong class="mobile-user-panel__name">{{ getName }}</strong>
                <span class="mobile-user-panel__email">{{ getEmail }}</span>
              </div>
            </div>

            <button
              v-if="canManageProfile"
              type="button"
              class="mobile-nav-link mobile-nav-link--button"
              @click="handleMobileProfile"
            >
              <span>Manage profile</span>
              <i class="fas fa-arrow-right" aria-hidden="true"></i>
            </button>

            <button
              type="button"
              class="mobile-nav-link mobile-nav-link--button"
              @click="handleMobileHome"
            >
              <span>Go to homepage</span>
              <i class="fas fa-arrow-right" aria-hidden="true"></i>
            </button>

            <button
              type="button"
              class="mobile-nav-link mobile-nav-link--button mobile-nav-link--danger"
              @click="handleMobileLogout"
            >
              <span>Logout</span>
              <i class="fas fa-sign-out-alt" aria-hidden="true"></i>
            </button>
          </div>
        </div>
      </div>
    </transition>
  </nav>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import NotificationPopup from '../popups/NotificationPopup.vue';
import { getNotifications } from '../../modules/admin/config/api-modules/api.notifications';

export default {
  name: 'Navbar',
  props: {
    showMobileAdminMenuButton: {
      type: Boolean,
      default: false,
    },
    adminMenuOpen: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    NotificationPopup,
  },
  data() {
    return {
      navLinks: [
        {
          title: 'Home',
          link: '/',
        },
        {
          title: 'About',
          link: '/ngo/about',
        },
        {
          title: 'FAQs',
          link: '/ngo/faq',
        },
        {
          title: 'Analysis',
          link: '/analysis',
        },
        {
          title: 'Contact Us',
          link: '/ngo/contact-us',
        },
      ],
      showNotifications: false,
      showUserDropdown: false,
      notifications: [],
      mobileNavOpen: false,
    };
  },
  computed: {
    ...mapGetters('auth', ['user', 'isLoggedIn', 'isAdmin']),
    isAdminViewer() {
      return [1, 2].includes(Number(this.user?.type));
    },
    canManageProfile() {
      return [1, 2, 5].includes(Number(this.user?.type));
    },
    getName() {
      if (this.user.username) {
        return this.user.username;
      }
      return this.user.name;
    },
    getEmail() {
      if (this.user.email) {
        return this.user.email;
      }
      return this.user.email;
    },
    userInitials() {
      const source = this.getName || 'User';
      return source
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part.charAt(0).toUpperCase())
        .join('');
    },
    marginLeft() {
      if (this.$route.path === '/admin/project') {
        return '0px';
      }
      return '48px';
    },
    marginRight() {
      if (this.$route.path === '/admin/project') {
        return '0px';
      }
      return '48px';
    },
    navheight() {
      if (this.$route.path === '/admin/project') {
        return 'auto';
      }
      return 'auto';
    },
  },
  watch: {
    $route() {
      this.closeMobileNav();
      this.showNotifications = false;
      this.showUserDropdown = false;
    },
    isLoggedIn: {
      immediate: true,
      handler(value) {
        if (value) {
          this.fetchNotifications();
          return;
        }

        this.notifications = [];
      },
    },
    'user.id': function watchUserId(value, oldValue) {
      if (value && value !== oldValue) {
        this.fetchNotifications();
      }
    },
  },
  methods: {
    ...mapActions('auth', ['LOGOUT']),
    normalizeResponse(response) {
      const records = response?.data?.results || response?.data || [];
      return Array.isArray(records) ? records : [];
    },
    formatNotificationTime(value) {
      if (!value) {
        return 'Just now';
      }

      const parsedDate = new Date(value);
      if (Number.isNaN(parsedDate.getTime())) {
        return 'Just now';
      }

      const diffInMs = Date.now() - parsedDate.getTime();
      const minute = 60 * 1000;
      const hour = 60 * minute;
      const day = 24 * hour;

      if (diffInMs < minute) {
        return 'Just now';
      }

      if (diffInMs < hour) {
        const minutes = Math.floor(diffInMs / minute);
        return `${minutes} min${minutes === 1 ? '' : 's'} ago`;
      }

      if (diffInMs < day) {
        const hours = Math.floor(diffInMs / hour);
        return `${hours} hour${hours === 1 ? '' : 's'} ago`;
      }

      if (diffInMs < 7 * day) {
        const days = Math.floor(diffInMs / day);
        return `${days} day${days === 1 ? '' : 's'} ago`;
      }

      return parsedDate.toLocaleDateString('en-NG', {
        day: 'numeric',
        month: 'short',
      });
    },
    mapNotificationItem(item, index) {
      return {
        id: item?.id || index,
        category: item?.is_global ? 'Platform' : 'Direct',
        time: this.formatNotificationTime(item?.created_at || item?.updated_at),
        title: item?.title || 'Notification',
        body: item?.message || 'No notification message was provided.',
        isRead: item?.is_read,
      };
    },
    async fetchNotifications() {
      if (!this.isLoggedIn || !this.user?.id) {
        this.notifications = [];
        return;
      }

      try {
        const response = await getNotifications(this.isAdminViewer ? null : this.user.id);
        const records = this.normalizeResponse(response);
        this.notifications = records.map((item, index) => this.mapNotificationItem(item, index));
      } catch (error) {
        this.notifications = [];
      }
    },
    toggleNotification() {
      this.closeMobileNav();
      this.showUserDropdown = false;
      this.showNotifications = !this.showNotifications;
    },
    async logoutButton() {
      await this.LOGOUT();
      this.$router.push('/auth/login');
    },
    closeNotification() {
      setTimeout(() => {
        this.showNotifications = false;
      }, 500);
    },
    toggleUserDetails() {
      this.closeMobileNav();
      this.showNotifications = false;
      this.showUserDropdown = !this.showUserDropdown;
    },
    closeUserDetails() {
      setTimeout(() => {
        this.showUserDropdown = false;
      }, 500);
    },
    toggleMobileNav() {
      this.showNotifications = false;
      this.showUserDropdown = false;
      this.mobileNavOpen = !this.mobileNavOpen;
    },
    closeMobileNav() {
      this.mobileNavOpen = false;
    },
    handleMobileDashboard() {
      this.closeMobileNav();
      this.$router.push('/organizations');
    },
    handleMobileProfile() {
      this.closeMobileNav();
      this.navigateToProfile();
    },
    handleMobileHome() {
      this.closeMobileNav();
      this.$router.push('/');
    },
    async handleMobileLogout() {
      this.closeMobileNav();
      await this.logoutButton();
    },
    navigateToProfile() {
      this.showUserDropdown = false;
      this.$router.push('/admin/profile');
    },
    navigateToHome() {
      const path = window.location.pathname;

      if (this.user.type === 1) {
        if (path !== '/admin/project') {
          this.$router.push('/admin/project/');
        }
      } else if (this.user.type === 2) {
        if (path !== '/admin/organization') {
          this.$router.push('/admin/organization/');
        }
      } else if (this.user.type === 5) {
        if (path !== '/admin/report/') {
          this.$router.push('/admin/report/');
        }
      } else if (path !== '/' && path !== '/ngo') {
        this.$router.push('/');
      }
    },
  },
};
</script>

<style scoped>
.premium-navbar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1030;
  padding: 14px 24px;
  background: linear-gradient(135deg, rgba(236, 245, 239, 0.96), rgba(255, 255, 255, 0.98)), #ffffff;
  border-bottom: 1px solid rgba(28, 69, 44, 0.12);
  box-shadow: 0 18px 45px rgba(16, 38, 23, 0.08);
  backdrop-filter: blur(16px);
}

.premium-navbar__inner {
  display: grid;
  grid-template-columns: minmax(0, 1.6fr) minmax(0, 1fr) auto;
  align-items: center;
  gap: 1.5rem;
  width: 100%;
  margin: 0 auto;
}

.premium-navbar__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  min-width: 0;
}

.brand-group {
  display: flex;
  align-items: center;
  gap: 1rem;
  min-width: 0;
}

.topbar-controls {
  display: none;
  align-items: center;
  gap: 0.75rem;
  flex-shrink: 0;
}

.mobile-nav-toggle,
.mobile-admin-menu {
  display: none;
  border: 0;
  border-radius: 18px;
  padding: 0.9rem 1rem;
  font-size: 0.88rem;
  font-weight: 700;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  box-shadow: 0 14px 28px rgba(31, 111, 51, 0.16);
  width: fit-content;
}

.mobile-nav-toggle {
  background: rgba(255, 255, 255, 0.92);
  color: #1f6f33;
  border: 1px solid rgba(28, 69, 44, 0.12);
}

.mobile-admin-menu {
  background: linear-gradient(135deg, #1f6f33, #31a54c);
  color: #ffffff;
}

.brand-mark {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 72px;
  height: 72px;
  margin-right: 0;
  padding: 0;
  border-radius: 22px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.98), rgba(235, 243, 237, 0.96));
  border: 1px solid rgba(48, 171, 76, 0.18);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.7), 0 16px 32px rgba(36, 92, 50, 0.12);
}

.brand-mark__image {
  width: 50px;
  height: 50px;
  object-fit: contain;
}

.brand-divider {
  width: 1px;
  align-self: stretch;
  background: linear-gradient(
    180deg,
    rgba(48, 171, 76, 0),
    rgba(48, 171, 76, 0.5),
    rgba(48, 171, 76, 0)
  );
}

.brand-copy {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 0;
  text-decoration: none;
}

.brand-copy__eyebrow {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: #2a8a43;
}

.brand-copy__title {
  font-size: 1.05rem;
  line-height: 1.2;
  font-weight: 700;
  color: #163322;
}

.brand-copy__subtitle {
  font-size: 0.82rem;
  line-height: 1.45;
  color: #547061;
  max-width: 640px;
}

.primary-nav {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  min-width: 0;
  flex-wrap: wrap;
}

.primary-nav__link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.75rem 0.95rem;
  border-radius: 999px;
  color: #2d5140;
  font-size: 0.84rem;
  font-weight: 700;
  text-decoration: none;
  transition: background-color 0.2s ease, color 0.2s ease, transform 0.2s ease;
}

.primary-nav__link:hover,
.primary-nav__link.router-link-exact-active {
  background: rgba(47, 159, 73, 0.1);
  color: #1f6f33;
  transform: translateY(-1px);
}

.nav-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 1rem;
  flex-shrink: 0;
}

.action-shell {
  position: relative;
}

.icon-button,
.user-wrapper {
  border: 0;
  outline: none;
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease,
    background-color 0.2s ease;
}

.icon-button {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: fit-content;
  height: 52px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.74);
  border: 1px solid rgba(28, 69, 44, 0.1);
  box-shadow: 0 14px 28px rgba(20, 55, 31, 0.08);
  color: #2f8f48;
  cursor: pointer;
}

.icon-button:hover,
.user-wrapper:hover,
.mobile-nav-toggle:hover,
.mobile-admin-menu:hover {
  transform: translateY(-1px);
  box-shadow: 0 20px 38px rgba(20, 55, 31, 0.12);
}

.icon-button i {
  font-size: 1.1rem;
}

.notification-count {
  position: absolute;
  top: 9px;
  right: 9px;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 999px;
  background: linear-gradient(135deg, #2e9d47, #1f6f33);
  color: #ffffff;
  font-size: 0.62rem;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 2px solid #ffffff;
}

.user-wrapper {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  min-height: 56px;
  padding: 0.6rem 0.95rem 0.6rem 0.7rem;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.76);
  border: 1px solid rgba(28, 69, 44, 0.1);
  box-shadow: 0 14px 28px rgba(20, 55, 31, 0.08);
  cursor: pointer;
}

.user-avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border-radius: 14px;
  background: linear-gradient(135deg, #2f9f49, #1f6d33);
  color: #ffffff;
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.08em;
}

.user-meta {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  line-height: 1.2;
}

.user-meta__label {
  font-size: 0.66rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #6e8979;
}

.user-meta__name {
  font-size: 0.92rem;
  font-weight: 700;
  color: #183523;
}

.user-meta__chevron {
  color: #6e8979;
  font-size: 0.78rem;
}

.overlay-panel {
  position: absolute;
  top: calc(100% + 12px);
  right: 0;
  overflow: hidden;
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.96);
  border: 1px solid rgba(21, 55, 33, 0.1);
  box-shadow: 0 26px 48px rgba(15, 38, 23, 0.12), 0 10px 24px rgba(15, 38, 23, 0.08);
  backdrop-filter: blur(16px);
}

.notifications-wrapper {
  width: 380px;
  padding: 0;
  background: transparent;
  border: 0;
  box-shadow: none;
  backdrop-filter: none;
}

.user-detail-wrapper {
  width: 280px;
}

.user-item-details p,
.user-logout span {
  margin: 0;
}

.user-item-details,
.user-logout {
  transition: background-color 0.2s ease;
}

.user-item-details:hover,
.user-logout:hover {
  background: rgba(236, 245, 239, 0.8);
}

.user-item-details {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  padding: 0.95rem 1.15rem;
  border-bottom: 1px solid rgba(21, 55, 33, 0.07);
  cursor: pointer;
}

.user-item-details--profile {
  background: linear-gradient(180deg, rgba(244, 249, 245, 0.95), rgba(255, 255, 255, 0.96));
}

.user-item-details p {
  font-size: 0.9rem;
  font-weight: 700;
  color: #183523;
}

.user-item-details span {
  font-size: 0.77rem;
  color: #70897a;
}

.user-logout {
  padding: 1rem 1.15rem;
  cursor: pointer;
}

.user-logout span {
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  font-size: 0.88rem;
  font-weight: 700;
  color: #c24f4f;
}

.guest-login__link,
.mobile-nav-cta {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.55rem;
  padding: 0.9rem 1.15rem;
  border-radius: 18px;
  background: linear-gradient(135deg, #1f6f33, #2fa049);
  color: #ffffff;
  text-decoration: none;
  font-size: 0.9rem;
  font-weight: 700;
  box-shadow: 0 16px 30px rgba(27, 92, 45, 0.2);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.guest-login__link:hover,
.mobile-nav-cta:hover {
  color: #ffffff;
  transform: translateY(-1px);
  box-shadow: 0 22px 36px rgba(27, 92, 45, 0.24);
}

.mobile-nav-cta--button {
  width: 100%;
  border: 0;
}

.mobile-nav-panel {
  display: none;
}

.mobile-notification-shell,
.mobile-user-panel {
  display: none;
}

@media (max-width: 1199px) {
  .premium-navbar__inner {
    grid-template-columns: minmax(0, 1.5fr) minmax(0, 1fr);
    grid-template-areas:
      'top actions'
      'nav nav';
  }

  .premium-navbar__top {
    grid-area: top;
  }

  .primary-nav {
    grid-area: nav;
    justify-content: flex-start;
  }

  .nav-actions {
    grid-area: actions;
  }
}

@media (max-width: 991px) {
  .premium-navbar {
    padding: 12px 16px;
  }

  .premium-navbar__inner {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 1rem;
  }

  .premium-navbar__top {
    width: 100%;
  }

  .brand-group,
  .nav-actions {
    width: 100%;
  }

  .topbar-controls {
    display: inline-flex;
  }

  .mobile-nav-toggle,
  .mobile-admin-menu {
    display: inline-flex;
    flex-shrink: 0;
  }

  .primary-nav {
    display: none;
  }

  .mobile-nav-panel {
    display: block;
    margin-top: 0.85rem;
    padding: 1rem;
    border-radius: 24px;
    background: rgba(255, 255, 255, 0.97);
    border: 1px solid rgba(28, 69, 44, 0.08);
    box-shadow: 0 20px 42px rgba(16, 38, 23, 0.1);
  }

  .mobile-nav-links {
    display: grid;
    gap: 0.75rem;
  }

  .mobile-nav-link {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    padding: 0.95rem 1rem;
    border-radius: 18px;
    background: linear-gradient(180deg, rgba(247, 251, 248, 0.96), rgba(239, 246, 241, 0.96));
    border: 1px solid rgba(28, 69, 44, 0.08);
    color: #1d3f2b;
    font-size: 0.9rem;
    font-weight: 700;
    text-decoration: none;
  }

  .mobile-nav-link.router-link-exact-active {
    background: linear-gradient(135deg, rgba(47, 159, 73, 0.14), rgba(47, 159, 73, 0.05));
    color: #1f6f33;
  }

  .mobile-nav-meta {
    margin-top: 1rem;
  }

  .mobile-nav-link--button {
    width: 100%;
    border: 0;
    cursor: pointer;
  }

  .mobile-user-panel {
    display: grid;
    gap: 0.75rem;
    margin-top: 1rem;
    padding-top: 1rem;
    border-top: 1px solid rgba(28, 69, 44, 0.08);
  }

  .mobile-user-panel__summary {
    display: flex;
    align-items: center;
    gap: 0.8rem;
    padding: 0.95rem 1rem;
    border-radius: 18px;
    background: linear-gradient(180deg, rgba(247, 251, 248, 0.96), rgba(239, 246, 241, 0.96));
    border: 1px solid rgba(28, 69, 44, 0.08);
  }

  .mobile-user-panel__meta {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  .mobile-user-panel__label {
    font-size: 0.62rem;
    font-weight: 700;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: #6e8979;
  }

  .mobile-user-panel__name {
    font-size: 0.95rem;
    color: #183523;
  }

  .mobile-user-panel__email {
    font-size: 0.75rem;
    color: #70897a;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .mobile-nav-link--danger {
    background: linear-gradient(180deg, rgba(253, 245, 245, 0.96), rgba(250, 239, 239, 0.96));
    color: #b33f3f;
  }

  .nav-actions {
    justify-content: space-between;
  }

  .guest-login {
    display: none;
  }

  .brand-copy__subtitle {
    max-width: none;
  }
}

@media (max-width: 767px) {
  .premium-navbar__inner {
    gap: 0.8rem;
    align-items: stretch;
  }

  .premium-navbar__top {
    gap: 0.8rem;
    width: 100%;
    align-items: center;
    justify-content: space-between;
  }

  .brand-group {
    align-items: center;
    gap: 0.8rem;
    width: auto;
  }

  .brand-copy {
    display: none;
  }

  .topbar-controls {
    width: fit-content;
    max-width: 100%;
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-start;
    gap: 0.75rem;
  }

  .mobile-notification-shell {
    display: block;
  }

  .mobile-nav-toggle span,
  .mobile-admin-menu span {
    display: none;
  }

  .brand-mark {
    width: 46px;
    height: 46px;
    border-radius: 16px;
    flex-shrink: 0;
  }

  .brand-mark__image {
    width: 30px;
    height: 30px;
  }

  .brand-divider {
    display: none;
  }

  .brand-copy__title {
    font-size: 0.92rem;
    line-height: 1.2;
  }

  .brand-copy__subtitle {
    font-size: 0.72rem;
    line-height: 1.35;
  }

  .brand-copy__eyebrow {
    font-size: 0.58rem;
    letter-spacing: 0.12em;
  }

  .nav-actions {
    display: none;
  }

  .action-shell {
    min-width: 0;
  }

  .icon-button {
    width: 48px;
    height: 48px;
    padding: 0;
    border-radius: 16px;
  }

  .notification-count {
    top: 7px;
    right: 7px;
  }

  .notifications-wrapper,
  .user-detail-wrapper {
    width: min(100vw - 32px, 320px);
    right: 0;
  }
}

@media (max-width: 620px) {
  .premium-navbar__top {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }

  .topbar-controls {
    width: auto;
    max-width: 100%;
    align-self: auto;
  }

  .mobile-nav-toggle,
  .mobile-admin-menu {
    width: fit-content;
    max-width: 100%;
    min-width: 48px;
    min-height: 48px;
    padding: 0.8rem;
    border-radius: 16px;
    font-size: 0.84rem;
    gap: 0;
  }
}

@media (max-width: 520px) {
  .brand-group {
    gap: 0.7rem;
  }

  .brand-mark {
    width: 48px;
    height: 48px;
  }

  .brand-mark__image {
    width: 32px;
    height: 32px;
  }

  .brand-copy__title {
    font-size: 0.8rem;
  }

  .brand-copy__subtitle {
    font-size: 0.66rem;
  }

  .user-meta {
    min-width: 0;
  }

  .user-meta__name {
    max-width: 10rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.mobile-nav-fade-enter-active,
.mobile-nav-fade-leave-active {
  transition: opacity 0.18s ease, transform 0.18s ease;
}

.mobile-nav-fade-enter,
.mobile-nav-fade-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}
</style>
